import React, { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, TrendingUp, Clock, ThumbsUp, Rocket, Award, LucideIcon } from 'lucide-react';
import { CATEGORICAL_PALETTE, STATUS_COLORS } from '../../utils/chartColors';

interface AnalyticsHeroCardProps {
  monthlyData: Array<{ month: string; deployments: number; successful: number; failed: number }>;
  dailyData: Array<{ date: string; deployments: number; pending: number; approved: number; deployed: number }>;
  moduleData: Array<{ name: string; value: number; color: string }>;
}

interface Metric {
  label: string;
  value: string | number;
  percent: number;
  color: string;
  icon: LucideIcon;
}

interface Bar {
  label: string;
  value: number;
}

const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);
const pct = (num: number, den: number) => (den > 0 ? Math.round((num / den) * 100) : 0);
const deltaPct = (current: number, previous: number) => (previous > 0 ? Math.round(((current - previous) / previous) * 100) : null);

const MiniBarChart: React.FC<{ bars: Bar[] }> = ({ bars }) => {
  const max = Math.max(...bars.map((b) => b.value), 1);
  return (
    <div className="flex items-end gap-1.5 h-16">
      {bars.map((b, i) => (
        <div key={i} className="flex flex-col items-center gap-1.5 w-5">
          <div
            className="w-full rounded-t-[3px]"
            style={{
              height: `${Math.max((b.value / max) * 100, 6)}%`,
              backgroundColor: i === bars.length - 1 ? '#2a78d6' : '#cde2fb',
            }}
          />
          <span className="text-[10px] text-gray-400">{b.label}</span>
        </div>
      ))}
    </div>
  );
};

const MetricTile: React.FC<{ metric: Metric }> = ({ metric }) => {
  const Icon = metric.icon;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
          style={{ backgroundColor: `${metric.color}1f` }}
        >
          <Icon className="w-4 h-4" style={{ color: metric.color }} />
        </span>
        <span className="text-xs text-gray-500 truncate">{metric.label}</span>
      </div>
      <p className="text-base font-bold text-gray-900 tabular-nums">{metric.value}</p>
      <div className="h-1.5 bg-gray-100 rounded-full mt-2 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(metric.percent, 100)}%`, backgroundColor: metric.color }}
        />
      </div>
    </div>
  );
};

// A rotating KPI card styled after a standard admin-template "Earnings Report"
// widget — headline figure + delta, a small bar chart, and a row of metric
// tiles each with its own progress bar — instead of the previous decorative
// gradient/orb treatment.
const AnalyticsHeroCard: React.FC<AnalyticsHeroCardProps> = ({ monthlyData, dailyData, moduleData }) => {
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  const thisMonth = monthlyData[monthlyData.length - 1] || { deployments: 0, successful: 0, failed: 0 };
  const prevMonth = monthlyData[monthlyData.length - 2];
  const thisMonthSuccessRate = pct(thisMonth.successful, thisMonth.successful + thisMonth.failed);
  const monthOverMonthDelta = deltaPct(thisMonth.deployments, prevMonth?.deployments ?? 0);

  const last30Total = sum(dailyData.map((d) => d.deployments));
  const last30Deployed = sum(dailyData.map((d) => d.deployed));
  const last30Pending = sum(dailyData.map((d) => d.pending));
  const last30Approved = sum(dailyData.map((d) => d.approved));
  const firstHalfTotal = sum(dailyData.slice(0, Math.floor(dailyData.length / 2)).map((d) => d.deployments));
  const secondHalfTotal = sum(dailyData.slice(Math.floor(dailyData.length / 2)).map((d) => d.deployments));
  const last30Delta = deltaPct(secondHalfTotal, firstHalfTotal);

  const allTimeTotal = sum(monthlyData.map((m) => m.deployments));
  const allTimeSuccess = sum(monthlyData.map((m) => m.successful));
  const allTimeFailed = sum(monthlyData.map((m) => m.failed));
  const topModule = [...moduleData].sort((a, b) => b.value - a.value)[0];

  const dailyBars: Bar[] = dailyData.slice(-7).map((d) => ({ label: d.date.slice(-2), value: d.deployments }));
  const weeklyBars: Bar[] = Array.from({ length: 6 }, (_, i) => {
    const chunk = dailyData.slice(i * 5, i * 5 + 5);
    return { label: `W${i + 1}`, value: sum(chunk.map((d) => d.deployments)) };
  });
  const monthlyBars: Bar[] = monthlyData.map((m) => ({ label: m.month.slice(0, 3), value: m.deployments }));

  const slides = [
    {
      title: 'This Month',
      subtitle: 'Deployment overview',
      headline: thisMonth.deployments,
      headlineLabel: 'Deployments',
      delta: monthOverMonthDelta,
      caption: 'compared to last month',
      bars: dailyBars,
      metrics: [
        { label: 'Successful', value: thisMonth.successful, percent: pct(thisMonth.successful, thisMonth.deployments), color: STATUS_COLORS.good, icon: CheckCircle2 },
        { label: 'Failed', value: thisMonth.failed, percent: pct(thisMonth.failed, thisMonth.deployments), color: STATUS_COLORS.critical, icon: XCircle },
        { label: 'Success rate', value: `${thisMonthSuccessRate}%`, percent: thisMonthSuccessRate, color: CATEGORICAL_PALETTE[0], icon: TrendingUp },
      ] as Metric[],
    },
    {
      title: 'Last 30 Days',
      subtitle: 'Pipeline overview',
      headline: last30Total,
      headlineLabel: 'Deployments',
      delta: last30Delta,
      caption: 'vs the prior 15 days',
      bars: weeklyBars,
      metrics: [
        { label: 'Pending', value: last30Pending, percent: pct(last30Pending, last30Total), color: STATUS_COLORS.warning, icon: Clock },
        { label: 'Approved', value: last30Approved, percent: pct(last30Approved, last30Total), color: CATEGORICAL_PALETTE[0], icon: ThumbsUp },
        { label: 'Deployed', value: last30Deployed, percent: pct(last30Deployed, last30Total), color: STATUS_COLORS.good, icon: Rocket },
      ] as Metric[],
    },
    {
      title: '6-Month Trend',
      subtitle: 'Deployment history',
      headline: allTimeTotal,
      headlineLabel: 'Total deployments',
      delta: monthOverMonthDelta,
      caption: 'vs previous month',
      bars: monthlyBars,
      metrics: [
        { label: 'Successful', value: allTimeSuccess, percent: pct(allTimeSuccess, allTimeTotal), color: STATUS_COLORS.good, icon: CheckCircle2 },
        { label: 'Failed', value: allTimeFailed, percent: pct(allTimeFailed, allTimeTotal), color: STATUS_COLORS.critical, icon: XCircle },
        { label: topModule ? topModule.name : 'Top module', value: topModule ? `${pct(topModule.value, allTimeTotal)}%` : '0%', percent: topModule ? pct(topModule.value, allTimeTotal) : 0, color: CATEGORICAL_PALETTE[6], icon: Award },
      ] as Metric[],
    },
  ];

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => setSlide((s) => (s + 1) % slides.length), 4500);
    return () => clearInterval(timer);
  }, [paused, slides.length]);

  return (
    <div
      className="relative bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slide dots */}
      <div className="absolute top-5 right-5 flex items-center gap-1.5 z-20">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => setSlide(i)}
            aria-label={`Show slide ${i + 1}`}
            className={`rounded-full transition-all ${i === slide ? 'w-2.5 h-2.5 bg-gray-700' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'}`}
          />
        ))}
      </div>

      {/* Sliding track — all three panels sit side by side and the whole strip
          translates, so slides visibly scroll past instead of instantly swapping. */}
      <div
        className="flex transition-transform duration-700 ease-in-out"
        style={{ transform: `translateX(-${slide * 100}%)` }}
      >
        {slides.map((s) => (
          <div key={s.title} className="w-full flex-shrink-0 p-6">
            <h3 className="text-base font-semibold text-gray-900">{s.title}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{s.subtitle}</p>

            <div className="flex items-start justify-between mt-5 gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-3xl font-bold text-gray-900">{s.headline}</span>
                  {s.delta !== null && (
                    <span
                      className={`px-2 py-0.5 rounded-md text-xs font-semibold ${
                        s.delta >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                      }`}
                    >
                      {s.delta >= 0 ? '+' : ''}
                      {s.delta}%
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2 max-w-[180px]">
                  {s.headlineLabel} {s.caption}
                </p>
              </div>
              <MiniBarChart bars={s.bars} />
            </div>

            <div className="mt-6 pt-5 border-t border-gray-100 grid grid-cols-3 gap-4">
              {s.metrics.map((m) => (
                <MetricTile key={m.label} metric={m} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnalyticsHeroCard;
